import React, { useState, useEffect } from 'react';
import {
  Hash,
  Plus,
  Edit2,
  Trash2,
  X,
  Save
} from 'lucide-react';
import { useToast } from '../contexts/ToastContext';

interface Matricola {
  _id: string;
  code: string;
  agenzia: string;
  provincia: string;
  motorizzazione: string;
  utenza: string;
}

interface STACode {
  _id: string;
  staCode: string;
  provincia: string;
  codice: string;
  matricole: Matricola[];
}

interface MatricolaFormData {
  agenzia: string;
  provincia: string;
  motorizzazione: string;
  utenza: string;
}

// API base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

const STACodeCard: React.FC = () => {
  const [staCodes, setStaCodes] = useState<STACode[]>([]);
  const toast = useToast();
  const [loading, setLoading] = useState(true);

  const [showSTAForm, setShowSTAForm] = useState(false);
  const [editingSTAId, setEditingSTAId] = useState<string | null>(null);
  const [staFormData, setSTAFormData] = useState('');

  const [showMatricolaForm, setShowMatricolaForm] = useState(false);
  const [editingMatricolaId, setEditingMatricolaId] = useState<string | null>(null);
  const [selectedSTACodeId, setSelectedSTACodeId] = useState<string | null>(null);
  const [matricolaFormData, setMatricolaFormData] = useState<MatricolaFormData>({
    agenzia: '',
    provincia: '',
    motorizzazione: '',
    utenza: ''
  });

  // Helper per ottenere headers con token
  const getAuthHeaders = () => {
    const token = localStorage.getItem('associate_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    };
  };

  // Carica codici STA
  const loadSTACodes = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/auth/sta-codes`, {
        headers: getAuthHeaders()
      });

      const data = await response.json();

      if (data.success) {
        setStaCodes(data.data);
      } else {
        throw new Error(data.message);
      }
    } catch (error: any) {
      console.error('Errore caricamento codici STA:', error);
      toast.error(error.message || 'Errore durante il caricamento');
    } finally {
      setLoading(false);
    }
  };

  // Carica all'avvio
  useEffect(() => {
    loadSTACodes();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, []);

  // Validazione codice STA (senza slash)
  const validateSTACode = (code: string): boolean => {
    return /^[A-Z]{2}\d{4}$/.test(code);
  };

  // Validazione matricola
  const validateMatricola = (data: MatricolaFormData): boolean => {
    return (
      /^[A-Z]{2}$/.test(data.agenzia) &&
      /^[A-Z]{2}$/.test(data.provincia) &&
      /^\d{4}$/.test(data.motorizzazione) &&
      /^\d{2}$/.test(data.utenza)
    );
  };

  // Salva codice STA
  const handleSaveSTACode = async () => {
    if (!staFormData || !validateSTACode(staFormData.toUpperCase())) {
      toast.error('Formato codice STA non valido. Usa il formato XX1234 (es. CH1234)');
      return;
    }

    try {
      const url = editingSTAId
        ? `${API_BASE_URL}/auth/sta-codes/${editingSTAId}`
        : `${API_BASE_URL}/auth/sta-codes`;

      const response = await fetch(url, {
        method: editingSTAId ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ staCode: staFormData.toUpperCase() })
      });

      const data = await response.json();

      if (data.success) {
        toast.success(editingSTAId ? 'Codice STA aggiornato' : 'Codice STA creato');
        setShowSTAForm(false);
        setEditingSTAId(null);
        setSTAFormData('');
        loadSTACodes();
      } else {
        throw new Error(data.message);
      }
    } catch (error: any) {
      toast.error(error.message || 'Errore durante il salvataggio');
    }
  };

  // Elimina codice STA
  const handleDeleteSTACode = async (id: string, code: string) => {
    if (!window.confirm(`Eliminare il codice STA ${code} e tutte le sue matricole?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/sta-codes/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Codice STA eliminato');
        loadSTACodes();
      } else {
        throw new Error(data.message);
      }
    } catch (error: any) {
      toast.error(error.message || 'Errore durante l\'eliminazione');
    }
  };

  // Salva matricola
  const handleSaveMatricola = async () => {
    if (!validateMatricola(matricolaFormData)) {
      toast.error('Dati matricola non validi. Controlla i formati');
      return;
    }

    if (!selectedSTACodeId) {
      toast.error('Nessun codice STA selezionato');
      return;
    }

    try {
      const matricolaData = {
        agenzia: matricolaFormData.agenzia.toUpperCase(),
        provincia: matricolaFormData.provincia.toUpperCase(),
        motorizzazione: matricolaFormData.motorizzazione,
        utenza: matricolaFormData.utenza
      };

      const url = editingMatricolaId
        ? `${API_BASE_URL}/auth/sta-codes/${selectedSTACodeId}/matricole/${editingMatricolaId}`
        : `${API_BASE_URL}/auth/sta-codes/${selectedSTACodeId}/matricole`;

      const response = await fetch(url, {
        method: editingMatricolaId ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(matricolaData)
      });

      const data = await response.json();

      if (data.success) {
        toast.success(editingMatricolaId ? 'Matricola aggiornata' : 'Matricola aggiunta');
        setShowMatricolaForm(false);
        setEditingMatricolaId(null);
        setSelectedSTACodeId(null);
        setMatricolaFormData({ agenzia: '', provincia: '', motorizzazione: '', utenza: '' });
        loadSTACodes();
      } else {
        throw new Error(data.message);
      }
    } catch (error: any) {
      toast.error(error.message || 'Errore durante il salvataggio');
    }
  };

  // Elimina matricola
  const handleDeleteMatricola = async (staCodeId: string, matricolaId: string, code: string) => {
    if (!window.confirm(`Eliminare la matricola ${code}?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/sta-codes/${staCodeId}/matricole/${matricolaId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Matricola eliminata');
        loadSTACodes();
      } else {
        throw new Error(data.message);
      }
    } catch (error: any) {
      toast.error(error.message || 'Errore durante l\'eliminazione');
    }
  };

  // Handler per aprire form
  const handleNewSTACode = () => {
    setEditingSTAId(null);
    setSTAFormData('');
    setShowSTAForm(true);
  };

  const handleEditSTACode = (staCode: STACode) => {
    setEditingSTAId(staCode._id);
    setSTAFormData(staCode.staCode);
    setShowSTAForm(true);
  };

  const handleNewMatricola = (staCodeId: string) => {
    setSelectedSTACodeId(staCodeId);
    setEditingMatricolaId(null);
    setMatricolaFormData({ agenzia: '', provincia: '', motorizzazione: '', utenza: '' });
    setShowMatricolaForm(true);
  };

  const handleEditMatricola = (staCodeId: string, matricola: Matricola) => {
    setSelectedSTACodeId(staCodeId);
    setEditingMatricolaId(matricola._id);
    setMatricolaFormData({
      agenzia: matricola.agenzia,
      provincia: matricola.provincia,
      motorizzazione: matricola.motorizzazione,
      utenza: matricola.utenza
    });
    setShowMatricolaForm(true);
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-800 p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
          <Hash className="w-5 h-5 text-white" />
        </div>
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">
          Codice STA e Matricola
        </h2>
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
        </div>
      )}

      {/* Lista Codici STA */}
      {!loading && (
        <div className="space-y-4">
          {staCodes.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
              Nessun codice STA inserito
            </p>
          ) : (
            staCodes.map((staCode) => (
              <div
                key={staCode._id}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50"
              >
                {/* Header Codice STA */}
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono font-bold text-base text-gray-900 dark:text-white">
                    {staCode.staCode}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditSTACode(staCode)}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30 rounded transition-colors"
                      title="Modifica codice STA"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteSTACode(staCode._id, staCode.staCode)}
                      className="p-1.5 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30 rounded transition-colors"
                      title="Elimina codice STA"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Lista Matricole */}
                <div className="space-y-2 mb-3">
                  {staCode.matricole.length === 0 ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                      Nessuna matricola associata
                    </p>
                  ) : (
                    staCode.matricole.map((matricola) => (
                      <div
                        key={matricola._id}
                        className="flex items-center justify-between bg-white dark:bg-gray-900 rounded px-3 py-2 border border-gray-200 dark:border-gray-700"
                      >
                        <span className="font-mono text-sm text-gray-900 dark:text-white">
                          {matricola.code}
                        </span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditMatricola(staCode._id, matricola)}
                            className="p-1 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30 rounded transition-colors"
                            title="Modifica matricola"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleDeleteMatricola(staCode._id, matricola._id, matricola.code)}
                            className="p-1 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30 rounded transition-colors"
                            title="Elimina matricola"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Bottone Aggiungi Matricola */}
                <button
                  onClick={() => handleNewMatricola(staCode._id)}
                  className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30 hover:bg-purple-100 dark:hover:bg-purple-900/50 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Aggiungi Matricola
                </button>
              </div>
            ))
          )}

          {/* Bottone Aggiungi Codice STA */}
          <button
            onClick={handleNewSTACode}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 rounded-lg shadow-sm transition-all"
          >
            <Plus className="w-5 h-5" />
            Aggiungi Codice STA
          </button>
        </div>
      )}

      {/* Modal Form Codice STA */}
      {showSTAForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-gray-900 dark:text-white">
                {editingSTAId ? 'Modifica Codice STA' : 'Nuovo Codice STA'}
              </h3>
              <button
                onClick={() => {
                  setShowSTAForm(false);
                  setEditingSTAId(null);
                  setSTAFormData('');
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Codice STA (formato: XX1234)
                </label>
                <input
                  type="text"
                  value={staFormData}
                  onChange={(e) => setSTAFormData(e.target.value.toUpperCase().replace('/', ''))}
                  placeholder="ES: CH1234"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono"
                  maxLength={6}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  2 lettere maiuscole (provincia) + 4 cifre
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowSTAForm(false);
                    setEditingSTAId(null);
                    setSTAFormData('');
                  }}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Annulla
                </button>
                <button
                  onClick={handleSaveSTACode}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
                >
                  <Save className="w-4 h-4" />
                  Salva
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Form Matricola */}
      {showMatricolaForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-gray-900 dark:text-white">
                {editingMatricolaId ? 'Modifica Matricola' : 'Nuova Matricola'}
              </h3>
              <button
                onClick={() => {
                  setShowMatricolaForm(false);
                  setEditingMatricolaId(null);
                  setSelectedSTACodeId(null);
                  setMatricolaFormData({ agenzia: '', provincia: '', motorizzazione: '', utenza: '' });
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Agenzia (2 lettere)
                  </label>
                  <input
                    type="text"
                    value={matricolaFormData.agenzia}
                    onChange={(e) => setMatricolaFormData({ ...matricolaFormData, agenzia: e.target.value.toUpperCase() })}
                    placeholder="AG"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono"
                    maxLength={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Provincia (2 lettere)
                  </label>
                  <input
                    type="text"
                    value={matricolaFormData.provincia}
                    onChange={(e) => setMatricolaFormData({ ...matricolaFormData, provincia: e.target.value.toUpperCase() })}
                    placeholder="CH"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono"
                    maxLength={2}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Motorizzazione (4 cifre)
                  </label>
                  <input
                    type="text"
                    value={matricolaFormData.motorizzazione}
                    onChange={(e) => setMatricolaFormData({ ...matricolaFormData, motorizzazione: e.target.value.replace(/\D/g, '') })}
                    placeholder="0053"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono"
                    maxLength={4}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Utenza (2 cifre)
                  </label>
                  <input
                    type="text"
                    value={matricolaFormData.utenza}
                    onChange={(e) => setMatricolaFormData({ ...matricolaFormData, utenza: e.target.value.replace(/\D/g, '') })}
                    placeholder="01"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono"
                    maxLength={2}
                  />
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Codice completo: <span className="font-mono font-bold">
                    {matricolaFormData.agenzia}{matricolaFormData.provincia}{matricolaFormData.motorizzazione}{matricolaFormData.utenza || '___'}
                  </span>
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowMatricolaForm(false);
                    setEditingMatricolaId(null);
                    setSelectedSTACodeId(null);
                    setMatricolaFormData({ agenzia: '', provincia: '', motorizzazione: '', utenza: '' });
                  }}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Annulla
                </button>
                <button
                  onClick={handleSaveMatricola}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
                >
                  <Save className="w-4 h-4" />
                  Salva
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default STACodeCard;
